import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import camelCase from 'lodash/camelCase'
import { useQuery } from '@apollo/client'
import { Skeleton } from 'antd'

import FormattedMessage from 'components/FormattedMessage'
import Hint from 'components/Hint'
import Icon from 'components/Icon'
import FormItem from 'components/Form/Item'

import { formatRanges, getMomentObjects } from 'helpers/date'
import { projectBrowsePathWithFilters } from 'helpers/projects'
import { isRequestFlowIntegratedWithNexus } from 'helpers/nexus'

import { PROJECT_DETAIL_QUERY } from './graphql'

import './index.less'

const EventDetailsOverview = (props) => {
  const {
    disableForm,
    enableForm,
    projectId,
    provider,
  } = props

  const { data, loading } = useQuery(PROJECT_DETAIL_QUERY, { variables: { projectId }, skip: !projectId })
  const isProjectIdValid = projectId && projectId !== '0'
  const isDataReady = !loading && isProjectIdValid
  const isCategorySupported = !!provider.eventCategories.find((eventCategory) => (
    eventCategory.isB2c === data?.project?.eventCategory?.isB2c
  ))

  useEffect(() => {
    if (!isDataReady || !isCategorySupported) {
      disableForm()
    } else {
      enableForm()
    }
  }, [isDataReady, isCategorySupported])

  return (
    <div className="contact-request__form__event-detail-overview">
      {isDataReady && !isCategorySupported && (
        <Hint variant="error">
          <FormattedMessage
            id="contactRequest.eventDetails.categoryIsNotSupported"
            values={{
              category: <FormattedMessage id={`event_categories.${data.project.eventCategory.name}`} />,
              link: (chunks) => <a className="link" href={projectBrowsePathWithFilters(data.project)}>{chunks}</a>,
            }}
          />
        </Hint>
      )}

      <FormItem label={<FormattedMessage id="contactRequestForm.eventCategoryId" />}>
        {isDataReady ? (
          <FormattedMessage id={`event_categories.${data.project.eventCategory.name}`} />
        ) : (
          <Skeleton active={isProjectIdValid} title={false} paragraph={{ rows: 1, width: '50%' }} />
        )}
      </FormItem>

      {(!isDataReady || data.project.requestedDates.length > 0) && (
        <FormItem label={<FormattedMessage id="contactRequestForm.requestedDates" />}>
          {isDataReady
            ? formatRanges(getMomentObjects(data.project.requestedDates), false).join(', ')
            : <Skeleton active={isProjectIdValid} title={false} paragraph={{ rows: 1, width: '70%' }} />
          }
        </FormItem>
      )}

      <FormItem label={<FormattedMessage id="contactRequestForm.numberOfPersons" />}>
        {isDataReady ? (
          <>
            <span>{data.project.guestCount}</span>
            {!isRequestFlowIntegratedWithNexus(data.project) && (
              <>
                <span>{'/'}</span>
                <Icon name={`seating-${data.project.roomType}`} />
                <FormattedMessage id={`shared.roomType.${camelCase(data.project.roomType)}`} />
              </>
            )}
          </>
        ) : <Skeleton active={isProjectIdValid} title={false} paragraph={{ rows: 1, width: '40%' }} />}
      </FormItem>

      <Hint className="contact-request__form__event-detail-overview__hint">
        <FormattedMessage id="contactRequest.eventDetails.hint" />
      </Hint>
    </div>
  )
}

EventDetailsOverview.propTypes = {
  disableForm: PropTypes.func.isRequired,
  enableForm: PropTypes.func.isRequired,
  projectId: PropTypes.string,
  provider: PropTypes.shape({
    eventCategories: PropTypes.arrayOf(PropTypes.shape({
      isB2c: PropTypes.bool,
    })).isRequired,
  }).isRequired,
}

export default EventDetailsOverview
